/* eslint-disable max-len */
/* eslint-disable complexity */
/* eslint-disable max-lines */
import { CastPlain } from '@/modules/casts/classes/CastPlain'
import firebase from 'firebase/app'
import { KeyDict } from '@/types'
import { CollectionNames, AdminDocNames } from '@/modules/database/utils'
import { Release } from '@/modules/ops/types/release'
import { CloudCastEvent, EventCast } from '@/types/events'
import { SourceStream, SourceStreamBroadCast, SourceStreamRemoteTalkBack, StreamType } from '@/types/streams'
import { AssetType } from '@/types/assets'
import { Widget } from '@/types/widgets'
import { cloneDeep, pickBy, reduce } from 'lodash'
import { LinkPointer } from '@/classes/LinkPointer/LinkPointer'
import { PointerTypes } from '@/classes/LinkPointer/LinkPointerBase'
import { Scene } from '@/types/scenes'
import { VideoPlayer } from '@/types/videoplayer'
import { CastApiErrorCode } from '../types/errors'
import { Cast, MinimumCastParams } from './cast'
import fb from '@/firebase'
import studio from '@/modules/database/utils'
import Convertable from '@/classes/Convertable'
import { CastInputStream, InputStreamType, isSourceStreamBroadcast, isSourceStreamCef } from '@/modules/streams/types'
import { sanitizeStringForUrl } from '@/modules/common/utils'
import { ParticipantInfo } from './ParticipantInfo'
import ActiveState from './ActiveState'
import { Result } from '@/modules/errorHandling/results'
import { CastType } from '@/types/casts'
import { VersionNumber } from '@/modules/classBuilder/mixins/hasVersion'
import { hasCastersMixin } from '@/modules/classBuilder/mixins/hasCasters'
import { CastReplaysDoc, ReplayConfigDoc } from '@/modules/replay/classes'
import KisweDate from '@/modules/common/classes/KisweDate'
import { set } from 'lodash'

export type CastParams = Pick<Cast, 'team_id'|'name'>
export type CastParamsSaveCast<C extends Cast> = Pick<C, 'name' | 'team_id' | 'invited_casters' | 'scenes' | 'widgets'
  | 'start_date' | 'end_date' | 'templateId' | 'isTempCast'>
  & Partial<Pick<C, 'originalCast' | 'regionalInfo' | 'notes' | 'scenesSettings'>>
  & { participants?: KeyDict<ParticipantInfo>, activeState?: ActiveState }
export type EditCastParams<C extends Cast> = { newCast: C, origCast: Cast, deleteUpdates: KeyDict<firebase.firestore.FieldValue> }
export type CastParamsWithDates = Pick<Cast, 'team_id' | 'name' | 'castType' | 'start_date' | 'end_date' | 'isTempCast'>
  & Partial<Pick<Cast, 'regionalInfo' | 'scenesSettings'>> & { castId: string }
export type CastParamsOptional = { pointerId?: string, tag?: string, skipTeamLastEventTimestamp?: boolean }

const preferredContentBoxes: Record<InputStreamType, string|null> = {
  [InputStreamType.Any]: null,
  [InputStreamType.Main]: 'main',
  [InputStreamType.Backup]: 'backup'
}

const constructBroadcastSourceStream = (type: InputStreamType): SourceStreamBroadCast => ({
  active: true,
  type: StreamType.Broadcast,
  volume: 0, volumeLeft: 0, volumeRight: 0,
  muted: false, leftMuted: false, rightMuted: false,
  preferred_content_box: preferredContentBoxes[type]
})

const constructRemoteTalkbackSourceStream = (): SourceStreamRemoteTalkBack => ({
  active: true,
  type: StreamType.RemoteTalkback,
  start_time: KisweDate.now()
})

const constructSourceStream = (id: string, stream: CastInputStream) => {
  if (id.toLowerCase().startsWith('director')) {
    return constructRemoteTalkbackSourceStream()
  } else {
    return constructBroadcastSourceStream(stream.type ?? InputStreamType.Any)
  }
}

export default abstract class CastAPI {
  protected _db: firebase.firestore.Firestore
  protected _currentCast: Cast
  protected _transaction: firebase.firestore.Transaction|null = null
  protected _teamId: string|null = null

  constructor (db: firebase.firestore.Firestore) {
    this._db = db
    // This is not nice, I need to refactor create Cast API anyway
    this._currentCast = Cast.create({
      name: '',
      team_id: '',
      start_date: new Date(),
      end_date: new Date(),
      castType: CastType.Plain
    }).unwrapOrFatal()
  }

  get currentCast () {
    return this._currentCast
  }

  abstract createCast (data: CastParams, currentUserId: string, optionalParams?: CastParamsOptional):
    Promise<Result<Cast, CastApiErrorCode>>
  abstract updateCast (castParams: Cast & { id: string }): Promise<Result<Cast, CastApiErrorCode>>

  protected async getDefaultVersion (): Promise<VersionNumber> {
    let teamVersion: VersionNumber = '1.0.0'
    if (this._teamId) {
      const result = await studio.db.collection(CollectionNames.TEAMS).doc(this._teamId).get()
      if (result.isSuccess) teamVersion = result.value.version ?? '1.0.0'
    }
    if (teamVersion !== undefined) return teamVersion
    const releaseDocRef = this._db.collection(CollectionNames.ADMIN).doc(AdminDocNames.RELEASE)
    let defaultVersion: VersionNumber = '1.0.0'
    const releases = await this._transaction?.get(releaseDocRef)
    if (releases?.exists) {
      const data = releases.data() as Release
      if (data?.default) defaultVersion = data.default
    }
    return defaultVersion
  }

  protected createBlankEvent (castParams: CastParamsWithDates, optionalParams?: CastParamsOptional): string {
    const eventDocRef = this._db.collection(CollectionNames.EVENTS).doc()
    const eventCast: EventCast = { castType: castParams.castType }
    if (castParams.regionalInfo?.fixed) eventCast.fixedRegion = true
    if (castParams.scenesSettings?.athlete.discipline) {
      set(eventCast, 'athlete.discipline', castParams.scenesSettings.athlete.discipline)
    }
    if (castParams.scenesSettings?.athlete.gender) {
      set(eventCast, 'athlete.gender', castParams.scenesSettings.athlete.gender)
    }

    const event: Partial<CloudCastEvent> = {
      team_id: castParams.team_id,
      name: castParams.name,
      asset_groups: {},
      deleted: false,
      start_date: castParams.start_date,
      end_date: castParams.end_date,
      isTempEvent: castParams.isTempCast,
      meta: { casts: { [castParams.castId]: eventCast } }
    }
    // @ts-ignore @tag does not exist on the CloudCastEvent class but we want to add it anyway in the DB.
    if (optionalParams?.tag) event['@tag'] = optionalParams.tag
    this._transaction?.set(eventDocRef, event)
    return eventDocRef.id
  }

  protected getAnonymousCasterRules () {
    const anonymousCasterRules = {
      max_anonymous_casters: 5,
      max_total_casters: 10,
      max_anonymous_accounts: 50
    }
    return anonymousCasterRules
  }

  protected setupSourceStreams (castParams: Partial<CastPlain>): KeyDict<SourceStream> {
    const sourceStreams: KeyDict<SourceStream> = {
      playlist: {
        active: true,
        start_time: Date.now() / 1000,
        type: StreamType.Playlist,
        volume: 100
      }
    }
    for (const [key, stream] of Object.entries(castParams.input_streams ?? {})) {
      sourceStreams[key] = constructSourceStream(key, stream)
      const existingSourceStream = castParams.source_streams?.[key]
      if (existingSourceStream) {
        sourceStreams[key] = { ...sourceStreams[key], ...existingSourceStream }
      }
    }
    return sourceStreams
  }

  protected cleanupBroadcastScenes (castParams: Partial<CastPlain>) {
    if (castParams.scenes === undefined) return castParams
    const scenes: KeyDict<Scene> = castParams.scenes
    for (const scene of Object.values(scenes)) {
      if (!scene.contents) continue
      for (const key in scene.contents) {
        const tmpContent = scene.contents[key]
        let shouldDeleteBroadcast = castParams.input_streams !== undefined
        shouldDeleteBroadcast = shouldDeleteBroadcast && castParams.input_streams?.[tmpContent.id] === undefined
        if (tmpContent.type === AssetType.Broadcast && shouldDeleteBroadcast) delete scene.contents[key]
      }
    }
    return castParams
  }

  protected async setupWidgets (castParams: Pick<Cast, 'team_id'|'id'|'widgets'>): Promise<KeyDict<Widget>> {
    const widgets: KeyDict<Widget> = {}
    if (Object.values(castParams.widgets ?? {}).length <= 0) return widgets
    const widgetDocRef = this._db.collection(CollectionNames.WIDGETS).doc()
    for (const widget of Object.values(castParams.widgets as KeyDict<Widget>)) {
      const isNewWidget = widget.id === widget.type && widget.type === 'chat'
      if (isNewWidget && this._transaction !== null) {
        this._transaction.set(widgetDocRef, {
          type: 'chat',
          team_id: castParams.team_id,
          cast_id: castParams.id,
          istyping: {},
          messages: []
        })
        widget.id = widgetDocRef.id
      }
      widgets[widget.id] = cloneDeep(widget)
    }
    return widgets
  }

  protected createPointerData (castId: string, castName: string, token: string, version: string,
    optionalParams?: CastParamsOptional)
  {
    if (!this._teamId) throw new Error('No team id')
    const pointerIdToReuse = optionalParams?.pointerId
    const pointerDocRef = pointerIdToReuse ? this._db.collection(CollectionNames.POINTERS).doc(pointerIdToReuse)
      : this._db.collection(CollectionNames.POINTERS).doc()
    const pointerData = LinkPointer.create({
      pointerType: PointerTypes.EVENT,
      eventId: castId,
      token,
      urlSuffix: sanitizeStringForUrl(castName),
      studioVersion: version,
      team_id: this._teamId
    })
    if (pointerData.isFailure) throw new Error(pointerData.message)
    if (optionalParams?.tag) {
      // @ts-ignore @tag does not exist on the EventLinkPointer class but we want to add it anyway in the DB.
      pointerData.value['@tag'] = optionalParams.tag
    }
    this._transaction?.set(pointerDocRef, Convertable.toObject(pointerData.value))
  }

  protected createVideoPlayer (castId: string) {
    const player1 = new VideoPlayer('player1').toJSON()
    const videoplayerDocRef = this._db.collection(CollectionNames.VIDEO_PLAYER).doc(castId)
    this._transaction?.set(videoplayerDocRef, { player1, team_id: this._teamId }, { merge: true })
  }

  protected createReplaySession (castId: string) {
    const replayConfigDoc = ReplayConfigDoc.newDocForCast(castId, this._teamId ?? '', 5) //TBD: Get from Cast Option UI
    const castReplaysDoc = CastReplaysDoc.newDocForCast(castId, this._teamId ?? '')
    const replayConfigDocRef = this._db.collection(CollectionNames.REPLAY_CONFIG).doc(castId)
    const castReplaysDocRef = this._db.collection(CollectionNames.CAST_REPLAYS).doc(castId)
    this._transaction?.set(replayConfigDocRef, replayConfigDoc)
    this._transaction?.set(castReplaysDocRef, castReplaysDoc)
  }

  protected setLastEventTimestamp () {
    if (this._teamId === null || this._teamId === '') throw new Error('No team id')
    const teamDocRef = this._db.collection(CollectionNames.TEAMS).doc(this._teamId)
    this._transaction?.set(teamDocRef, { lastEventTimestamp: Date.now() }, { merge: true })
  }

  protected clearCastPropsForCopy (cast: Partial<Cast>) {
    delete cast.id
    delete cast.k360_event_id
    delete cast.k360_event_id_backup
    delete cast.event_id
    delete cast.status
    delete cast.ngcvp_status
    delete cast.m3u8
    delete cast.video_start_time
    delete cast.video_start_time_backup
    delete cast.chat_id
    delete cast.token
    if (hasCastersMixin(cast)) {
      cast.moderators = {}
    }
    cast.self_muted = {}
    cast.custom_rtmp_destinations = {}
    cast.output_streams = {}
    cast.feedname = {}
    cast.online_sessions = {}
    cast.preview_scenes = {}
    cast.talk_back = {}
    // TODO: Should we clear `messages`?
  }

  // eslint-disable-next-line max-lines-per-function
  protected cleanAndCopyOriginalCastProps (cast: Partial<Cast>) {
    const originalCast: Partial<Cast> = JSON.parse(cast.originalCast ?? '{}')
    cast.widgets = cloneDeep(originalCast.widgets || {})
    // sourcestreams
    cast.source_streams = reduce(
      cloneDeep(originalCast.source_streams),
      (result: KeyDict<SourceStream>, value, key) => {
        if (key === 'playlist' || isSourceStreamBroadcast(value) || isSourceStreamCef(value)) {
          // @ts-ignore // copy specific properties only
          result[key] = {
            active: !!value.active,
            start_time: Date.now() / 1000,
            type: value.type ?? StreamType.Broadcast,
            volume: value.volume || 0,
            volumeLeft: value.volumeLeft || 0,
            volumeRight: value.volumeRight || 0,
            muted: !!value.muted,
            leftMuted: !!value.leftMuted,
            rightMuted: !!value.rightMuted
          }
          if (isSourceStreamCef(value)) {
            // @ts-ignore
            result[key].castId = value.castId
          }
        }
        return result
      },
      {}
    )
    // Remove anonymous casters
    cast.invited_casters = pickBy(cast.invited_casters, (caster) => !caster.anonymous)
  }

  public async getCopyOfCast (castId: string): Promise<Result<Cast, CastApiErrorCode>> {
    try {
      const docRef = await this._db.collection(CollectionNames.CASTS).doc(castId).get()
      const data = docRef.data() as Cast
      if (data === undefined) {
        return Result.fail(CastApiErrorCode.GET_COPY, `Cast with id ${ castId } not found`)
      }
      const castLoad = Cast.create({
        ...data, name: `${ data.name } [COPY]`,
        originalCast: JSON.stringify({ ...data, originalCast: null })
      })
      if (castLoad.isFailure) {
        return Result.fail(CastApiErrorCode.GET_COPY, 'Could not get Copy of Cast')
      }
      const cast = castLoad.value
      const now = new Date()
      if (cast.start_date < now) cast.start_date = now
      if (cast.end_date < cast.start_date) cast.end_date = new Date(cast.start_date.getTime() + 4 * 60 * 60 * 1000)

      this.clearCastPropsForCopy(cast)
      this.cleanAndCopyOriginalCastProps(cast)

      return Result.success(cast)
    } catch (error) {
      return Result.fail(CastApiErrorCode.GET_COPY, `Cannot get a copy of cast ${ castId }: ${ error }`)
    }
  }

  private updateExistingCastStreams ({ newCast, origCast, deleteUpdates }: EditCastParams<Cast>): EditCastParams<Cast> {
    if (newCast.custom_rtmp_destinations !== undefined) {
      for (const outputId of Object.keys(origCast.custom_rtmp_destinations ?? {})) {
        if (newCast.custom_rtmp_destinations?.[outputId] !== undefined) continue
        deleteUpdates['custom_rtmp_destinations.' + outputId] = fb.deleteField
      }
    }
    if (newCast.input_streams === undefined) return { newCast, origCast, deleteUpdates }
    for (const key of Object.keys(origCast.input_streams)) {
      if (key in newCast.input_streams) continue
      deleteUpdates['input_streams.' + key] = fb.deleteField
    }
    for (const [key, stream] of Object.entries(newCast.input_streams)) {
      if (newCast.source_streams[key] !== undefined) continue
      newCast.source_streams[key] = constructSourceStream(key, stream)
    }
    for (const [key, stream] of Object.entries(origCast.source_streams)) {
      if (stream.type === StreamType.Broadcast && !(key in newCast.input_streams)) {
        deleteUpdates['source_streams.' + key] = fb.deleteField
        delete origCast.source_streams[key]
        delete newCast.source_streams[key]
      }
    }
    return { newCast, origCast, deleteUpdates }
  }

  private async updateExistingCastWidgetsAssets ({ newCast, origCast, deleteUpdates }: EditCastParams<Cast>
                                                ): Promise<EditCastParams<Cast>> {
    for (const key of Object.keys(origCast.widgets ?? {})) {
      if (key in newCast.widgets) continue
      deleteUpdates['widgets.' + key] = fb.deleteField
    }
    const updateWidgets = cloneDeep(newCast.widgets ?? {})
    newCast.widgets = {}
    if (updateWidgets && Object.values(updateWidgets).length > 0) {
      for (const widget of Object.values(updateWidgets as KeyDict<Widget>) as Widget[]) {
        const missingId = !widget.id || widget.id === widget.type
        if (missingId) {
          /* widget.id = await store.dispatch.chatwidget.create({
            teamid: newCast.team_id ?? origCast.team_id,
            castid: newCast.id
          })*/
        }
        newCast.widgets[widget.id] = JSON.parse(JSON.stringify(widget))
      }
    }
    if (origCast.asset_groups !== undefined && newCast.asset_groups !== undefined)
    for (const assetgroup in origCast.asset_groups) {
      if (!(assetgroup in newCast.asset_groups)) {
        deleteUpdates['asset_groups.' + assetgroup] = fb.deleteField
      }
    }
    return { newCast, origCast, deleteUpdates }
  }

  private updateExistingCastScenes ({ newCast, origCast, deleteUpdates }: EditCastParams<Cast>): EditCastParams<Cast> {
    // for (const [sceneId, scene] of Object.entries(origCast.scenes)) {
    //   if (!(sceneId in newCast.scenes)) {
    //     deleteUpdates['scenes.' + sceneId] = fb.deleteField
    //     continue
    //   }

    //   for (const contentId of Object.keys(scene.contents ?? {})) {
    //     const newCastContents = newCast.scenes[sceneId].contents ?? {}
    //     if (contentId in newCastContents) continue
    //     deleteUpdates['scenes.' + sceneId + '.contents.' + contentId] = fb.deleteField
    //   }
    // }

    // Replace the scenes with those of the new cast, instead of individually deleting removing old scenes and contents
    // (and then later merging in the new things). This is somewhat a workaround for scenes otherwise becoming empty
    // during the delete update step when replacing the scenes with new ones from a different template. Which the
    // firestore rules would otherwise block.
    // Note: This assumes that the newCast's scenes are "complete" and not partial info about scenes.
    // @ts-ignore Because TypeScript doesn't support the following type definition:
    //            deleteUpdates: KeyDict<firestore.FieldValue> | { activeScene: string, scenes: KeyDict<Scene> }
    //            And we would have to do KeyDict<firestore.FieldValue|KeyDict<Scene>|string> which isn't ideal either.
    deleteUpdates.activeScene = newCast.activeScene
    // @ts-ignore
    deleteUpdates.scenes = newCast.scenes

    return { newCast, origCast, deleteUpdates }
  }

  public updateExistingCastInvitedCasters (editCastParams: EditCastParams<Cast>): EditCastParams<Cast> {
    const { newCast, origCast, deleteUpdates } = editCastParams
    for (const casterId of Object.keys(origCast.invited_casters ?? {})) {
      if (newCast.invited_casters === null) continue
      if (casterId in newCast.invited_casters) continue
      deleteUpdates['invited_casters.' + casterId] = fb.deleteField
    }
    return { newCast, origCast, deleteUpdates }
  }

  private async updateExistingEvent (eventParams: Partial<CloudCastEvent>): Promise<Result<Partial<CloudCastEvent>, CastApiErrorCode>> {
    try {
      if (eventParams.id === undefined || eventParams.start_date === undefined || eventParams.end_date === undefined) {
        return Result.fail(CastApiErrorCode.UPDATE_EVENT, 'No event id, start_date or end_date given')
      }
      const eventDocRef = await this._db.collection(CollectionNames.EVENTS).doc(eventParams.id).get()
      if (eventDocRef.exists === false || eventDocRef.data() === undefined) {
        return Result.fail(CastApiErrorCode.UPDATE_EVENT, `Event with id ${ eventParams.id } not found`)
      }
      await this._db.collection(CollectionNames.EVENTS).doc(eventParams.id).update(Convertable.toObject(eventParams))
      return Result.success(eventParams)
    } catch (error) {
      return Result.fail(CastApiErrorCode.UPDATE_EVENT, `Cannot update event ${ eventParams.id }: ${ error }`)
    }
  }

  // eslint-disable-next-line max-lines-per-function
  public async updateExistingCast <T extends CastType> (castParams: MinimumCastParams<T>):
    Promise<Result<Cast, CastApiErrorCode>> {
    try {
      const originalDocRef = await this._db.collection(CollectionNames.CASTS).doc(castParams.id).get()
      if (originalDocRef.exists === false || originalDocRef.data() === undefined) {
        return Result.fail(CastApiErrorCode.UPDATE_CAST, `Cast with id ${ castParams.id } not found`)
      }
      const origCast = Cast.create(originalDocRef.data() as MinimumCastParams<T>)
      if (origCast.isFailure) return Result.fail(CastApiErrorCode.UPDATE_CAST, 'Could not load orig cast')
      const loadNewCast = Cast.create(castParams)
      if (loadNewCast.isFailure) return Result.fail(CastApiErrorCode.UPDATE_CAST, 'Could not load new cast')
      const initialUpdates = {
        origCast: origCast.value,
        newCast: loadNewCast.value,
        deleteUpdates: {}
      }
      const castStreamUpdates = this.updateExistingCastStreams(initialUpdates)
      const castWidgetUpdates = await this.updateExistingCastWidgetsAssets(castStreamUpdates)
      const castScenesUpdates = this.updateExistingCastScenes(castWidgetUpdates)
      const castCasterUpdates = this.updateExistingCastInvitedCasters(castScenesUpdates)
      const { newCast, deleteUpdates } = castCasterUpdates
      if (newCast.start_date !== undefined && newCast.end_date !== undefined && newCast.start_date > newCast.end_date) {
        newCast.end_date = newCast.start_date
      }
      const cast = Convertable.toObject(newCast)
      await this._db.collection(CollectionNames.CASTS).doc(newCast.id).update(Convertable.toObject(deleteUpdates))
      await this._db.collection(CollectionNames.CASTS).doc(newCast.id).set(cast, { merge: true })
      const updatedEvent = await this.updateExistingEvent({
        id: newCast.event_id,
        name: newCast.name,
        start_date: newCast.start_date,
        end_date: newCast.end_date
      })
      if (updatedEvent.isFailure) return Result.fail(CastApiErrorCode.UPDATE_EVENT, updatedEvent.error)
      return Result.success(newCast)
    } catch (error) {
      return Result.fail(CastApiErrorCode.UPDATE_CAST, `Cannot update cast ${ castParams.id }: ${ error }`)
    }
  }
}
